import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, ApiUser, tokenManager } from '../services/api';

interface AuthContextType {
  user: ApiUser | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<ApiUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = async () => {
    try {
      // Check if we have tokens first
      const hasRefreshToken = !!tokenManager.getRefreshToken();
      const hasAccessToken = !!tokenManager.getAccessToken();
      
      if (!hasRefreshToken && !hasAccessToken) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      const response = await authApi.getCurrentUser();
      
      // Save tokens from session if available
      if (response.session?.access_token) {
        tokenManager.setAccessToken(response.session.access_token);
      }
      if (response.session?.refresh_token) {
        tokenManager.setRefreshToken(response.session.refresh_token);
      }
      
      setUser(response.user);
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      tokenManager.clearTokens();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (usernameOrEmail: string, password: string) => {
    try {
      const response = await authApi.login(usernameOrEmail, password);
      
      // Save tokens from session if available
      if (response.session?.access_token) {
        tokenManager.setAccessToken(response.session.access_token);
      }
      if (response.session?.refresh_token) {
        tokenManager.setRefreshToken(response.session.refresh_token);
      }
      
      setUser(response.user);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      tokenManager.clearTokens();
    }
  };

  // Handle automatic logout from token refresh failures
  const handleAutoLogout = () => {
    setUser(null);
    tokenManager.clearTokens();
  };

  useEffect(() => {
    checkAuthStatus();

    // Listen for automatic logout events from the API layer
    const handleAuthLogout = () => handleAutoLogout();
    window.addEventListener('auth:logout', handleAuthLogout);

    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, []);

  const value: AuthContextType = {
    user,
    isLoggedIn: !!user,
    isLoading,
    login,
    logout,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};